import type { ResultOf } from '$lib/erar/api';
import type ERAR from '$lib/erar/spec';

export enum Tier_Before_0_78_0 {
  None,
  Iron,
  Bronze,
  Silver,
  Gold,
  Platinum,
  Diamond,
  Demigod,
  Eternity,
}

export enum Tier {
  None,
  Iron,
  Bronze,
  Silver,
  Gold,
  Platinum,
  Diamond,
  Meteorite,
  Mithril,
  Demigod,
  Eternity,
}

export enum Division {
  None,
  I,
  II,
  III,
  IV,
}

const ETERNITY_START_MMR_BEFORE_0_78_0 = 2600;
const DEMIGOD_START_MMR_BEFORE_0_78_0 = 2400;

const ETERNITY_START_MMR_BEFORE_1_0 = 2600;
const MITHRIL_START_MMR_BEFORE_1_0 = 2400;

const ETERNITY_START_MMR_AFTER_1_0 = 6000;
const MITHRIL_START_MMR_AFTER_1_0 = 6000;

const ETERNITY_START_MMR_AFTER_1_3 = 6400;
const MITHRIL_START_MMR_AFTER_1_3 = 6200;

const ETERNITY_START_MMR_AFTER_1_22 = 7000;
const MITHRIL_START_MMR_AFTER_1_22 = 6800;

const METEORITE_START_MMR_AFTER_1_29 = 6400;

/**
 * 0.78.0 버전에 미스릴 티어가 추가되어서 UI 표시에 필요한 Tier[7]의 결과가 Mithril / Demigod으로 달라지게 되었으므로
 * 들고 있는 정보가 Tier인지 Tier_Before_0_78_0인지 문자열 정보를 사용하는 쪽에서 신경쓸 필요가 없도록 tier와 tierName을 묶어서 결과로 반환한다.
 */
interface GetRankTierResult {
  tierEnum: Tier | Tier_Before_0_78_0;
  tierName: string;
}

export function getRankTierByQueue(
  queue?: ResultOf<'GET /queues/by-player/[id]'>['result'][number],
) {
  return getRankTier({
    ...queue,
    flags: { isDemigod: queue?.isDemigod ?? false, isEternity: queue?.isEternity ?? false },
  });
}

export function getRankTierByLightQueue(
  queue?: ERAR.Schema.LightQueueEntity & { seasonId: number },
) {
  return getRankTier({
    ...queue,
    flags: { isDemigod: queue?.isDemigod ?? false, isEternity: queue?.isEternity ?? false },
  });
}

/**
 * @member mmr 플레이어의 mmr
 * @member seasonId 시즌아이디(프리시즌 포함), 일반이면 0
 * @member isEternity 이터니티 여부(상위 200위), 서버에서 내려주는 값을 그대로 사용해야 함
 * @member isDemigod 데미갓 여부(0.78.0 부터는 상위 700위, 그 이전에는 2400점 이상), 서버에서 내려주는 값을 그대로 사용해야 함, 0.78.0(시즌15도중) 이전 데이터에는 존재하지 않음
 */
export interface GetRankTierParam {
  mmr?: number;
  seasonId?: number;
  flags?: ERAR.Schema.MatchParticipation['recordedMMRFlags'];
}

export interface RankTier {
  tier: GetRankTierResult;
  division: Division;
  lp: number;
  fullLp: number;
}

export function getRankTier({ mmr, seasonId, flags }: GetRankTierParam): RankTier {
  if (mmr === undefined || mmr <= 0) {
    return {
      tier: { tierEnum: Tier.None, tierName: Tier[Tier.None] },
      division: Division.None,
      lp: 0,
      fullLp: 0,
    };
  }

  if (seasonId !== undefined && seasonId <= 2) {
    return getRankBeforeSeason2(mmr);
  }

  if (seasonId !== undefined && seasonId < 15) {
    return getRankBefore0_78_0(mmr, flags?.isEternity ?? false);
  }

  if (seasonId !== undefined && seasonId < 18) {
    return getRankBefore1_0(mmr, flags?.isEternity ?? false, flags?.isDemigod ?? false);
  }

  if (seasonId !== undefined && seasonId < 23) {
    return getRankAfter1_0(mmr, flags?.isEternity ?? false, flags?.isDemigod ?? false);
  }

  if (seasonId !== undefined && seasonId < 25) {
    return getRankAfter1_3(mmr, flags?.isEternity ?? false, flags?.isDemigod ?? false);
  }

  if (seasonId !== undefined && seasonId < 27) {
    return getRankAfter1_22(mmr, flags?.isEternity ?? false, flags?.isDemigod ?? false);
  }

  return getRankAfter1_29(mmr, flags?.isEternity ?? false, flags?.isDemigod ?? false);
}

function getRankBeforeSeason2(mmr: number) {
  let tierEnum: Tier_Before_0_78_0;
  let division: Division;
  let lp: number;

  if (mmr >= 3100) {
    tierEnum = Tier_Before_0_78_0.Eternity;
    division = Division.I;
    lp = mmr - 3100;
  } else {
    tierEnum = Math.min(8, 1 + Math.floor(mmr / 400));
    division = Math.max(1, 4 - Math.floor((mmr - 400 * (tierEnum - 1)) / 100));
    lp = mmr % 100;
  }

  const tierName = Tier_Before_0_78_0[tierEnum];
  return { tier: { tierEnum, tierName }, division, lp, fullLp: mmr };
}

function getRankBefore0_78_0(mmr: number, isEternity: boolean) {
  let tierEnum: Tier_Before_0_78_0;
  let division: Division;
  let lp: number;

  if (isEternity && mmr >= ETERNITY_START_MMR_BEFORE_0_78_0) {
    tierEnum = Tier_Before_0_78_0.Eternity;
    division = Division.None;
    lp = mmr - ETERNITY_START_MMR_BEFORE_0_78_0;
  } else if (mmr >= DEMIGOD_START_MMR_BEFORE_0_78_0) {
    tierEnum = Tier_Before_0_78_0.Demigod;
    division = Division.None;
    lp = mmr - DEMIGOD_START_MMR_BEFORE_0_78_0;
  } else {
    tierEnum = Math.min(8, 1 + Math.floor(mmr / 400));
    division = Math.max(1, 4 - Math.floor((mmr - 400 * (tierEnum - 1)) / 100));
    lp = mmr % 100;
  }

  const tierName = Tier_Before_0_78_0[tierEnum];
  return { tier: { tierEnum, tierName }, division, lp, fullLp: mmr };
}

function getRankBefore1_0(mmr: number, isEternity: boolean, isDemigod: boolean) {
  let tierEnum: Tier;
  let division: Division;
  let lp: number;

  if (isEternity && mmr >= ETERNITY_START_MMR_BEFORE_1_0) {
    tierEnum = Tier.Eternity;
    division = Division.None;
    lp = mmr - ETERNITY_START_MMR_BEFORE_1_0;
  } else if (mmr >= MITHRIL_START_MMR_BEFORE_1_0) {
    tierEnum = isDemigod ? Tier.Demigod : Tier.Mithril;
    division = Division.None;
    lp = mmr - MITHRIL_START_MMR_BEFORE_1_0;
  } else {
    tierEnum = Math.min(8, 1 + Math.floor(mmr / 400));
    division = Math.max(1, 4 - Math.floor((mmr - 400 * (tierEnum - 1)) / 100));
    lp = mmr % 100;
  }

  const tierName = Tier[tierEnum];
  return { tier: { tierEnum, tierName }, division, lp, fullLp: mmr };
}

function getRankAfter1_0(mmr: number, isEternity: boolean, isDemigod: boolean) {
  let tierEnum: Tier;
  let division: Division;

  if (isEternity && mmr >= ETERNITY_START_MMR_AFTER_1_0) {
    tierEnum = Tier.Eternity;
    division = Division.None;
  } else if (mmr >= MITHRIL_START_MMR_AFTER_1_0) {
    tierEnum = isDemigod ? Tier.Demigod : Tier.Mithril;
    division = Division.None;
  } else {
    tierEnum = Math.min(8, 1 + Math.floor(mmr / 1000));
    division = Math.max(1, 4 - Math.floor((mmr - 1000 * (tierEnum - 1)) / 250));
  }

  const tierName = Tier[tierEnum];
  return { tier: { tierEnum, tierName }, division, lp: mmr, fullLp: mmr };
}

function getRankAfter1_3(mmr: number, isEternity: boolean, isDemigod: boolean) {
  let tierEnum: Tier;
  let division: Division;

  if (isEternity && mmr >= ETERNITY_START_MMR_AFTER_1_3) {
    tierEnum = Tier.Eternity;
    division = Division.None;
  } else if (mmr >= MITHRIL_START_MMR_AFTER_1_3) {
    tierEnum = isDemigod ? Tier.Demigod : Tier.Mithril;
    division = Division.None;
  } else if (mmr >= 4800) {
    tierEnum = Tier.Diamond;
    division = Math.max(1, 4 - Math.floor((mmr - 4800) / 350));
  } else if (mmr >= 3600) {
    tierEnum = Tier.Platinum;
    division = Math.max(1, 4 - Math.floor((mmr - 3600) / 300));
  } else if (mmr >= 2600) {
    tierEnum = Tier.Gold;
    division = Math.max(1, 4 - Math.floor((mmr - 2600) / 250));
  } else if (mmr >= 1600) {
    tierEnum = Tier.Silver;
    division = Math.max(1, 4 - Math.floor((mmr - 1600) / 250));
  } else if (mmr >= 800) {
    tierEnum = Tier.Bronze;
    division = Math.max(1, 4 - Math.floor((mmr - 800) / 200));
  } else {
    tierEnum = Tier.Iron;
    division = Math.max(1, 4 - Math.floor(mmr / 200));
  }

  const tierName = Tier[tierEnum];
  return { tier: { tierEnum, tierName }, division, lp: mmr, fullLp: mmr };
}

function getRankAfter1_22(mmr: number, isEternity: boolean, isDemigod: boolean) {
  let tierEnum: Tier;
  let division: Division;

  if (isEternity && mmr >= ETERNITY_START_MMR_AFTER_1_22) {
    tierEnum = Tier.Eternity;
    division = Division.None;
  } else if (mmr >= MITHRIL_START_MMR_AFTER_1_22) {
    tierEnum = isDemigod ? Tier.Demigod : Tier.Mithril;
    division = Division.None;
  } else if (mmr >= 5200) {
    tierEnum = Tier.Diamond;
    division = Math.max(1, 4 - Math.floor((mmr - 5200) / 400));
  } else if (mmr >= 3800) {
    tierEnum = Tier.Platinum;
    division = Math.max(1, 4 - Math.floor((mmr - 3800) / 350));
  } else if (mmr >= 2600) {
    tierEnum = Tier.Gold;
    division = Math.max(1, 4 - Math.floor((mmr - 2600) / 300));
  } else if (mmr >= 1600) {
    tierEnum = Tier.Silver;
    division = Math.max(1, 4 - Math.floor((mmr - 1600) / 250));
  } else if (mmr >= 800) {
    tierEnum = Tier.Bronze;
    division = Math.max(1, 4 - Math.floor((mmr - 800) / 200));
  } else {
    tierEnum = Tier.Iron;
    division = Math.max(1, 4 - Math.floor(mmr / 200));
  }

  const tierName = Tier[tierEnum];
  return { tier: { tierEnum, tierName }, division, lp: mmr, fullLp: mmr };
}

function getRankAfter1_29(mmr: number, isEternity: boolean, isDemigod: boolean) {
  let tierEnum: Tier;
  let division: Division;

  if (isEternity && mmr >= ETERNITY_START_MMR_AFTER_1_22) {
    tierEnum = Tier.Eternity;
    division = Division.None;
  } else if (mmr >= MITHRIL_START_MMR_AFTER_1_22) {
    tierEnum = isDemigod ? Tier.Demigod : Tier.Mithril;
    division = Division.None;
  } else if (mmr >= METEORITE_START_MMR_AFTER_1_29) {
    tierEnum = Tier.Meteorite;
    division = Division.None;
  } else if (mmr >= 5000) {
    tierEnum = Tier.Diamond;
    division = Math.max(1, 4 - Math.floor((mmr - 5000) / 350));
  } else if (mmr >= 3600) {
    tierEnum = Tier.Platinum;
    division = Math.max(1, 4 - Math.floor((mmr - 3600) / 350));
  } else if (mmr >= 2400) {
    tierEnum = Tier.Gold;
    division = Math.max(1, 4 - Math.floor((mmr - 2400) / 300));
  } else if (mmr >= 1400) {
    tierEnum = Tier.Silver;
    division = Math.max(1, 4 - Math.floor((mmr - 1400) / 250));
  } else if (mmr >= 600) {
    tierEnum = Tier.Bronze;
    division = Math.max(1, 4 - Math.floor((mmr - 600) / 200));
  } else {
    tierEnum = Tier.Iron;
    division = Math.max(1, 4 - Math.floor(mmr / 150));
  }

  const tierName = Tier[tierEnum];
  return { tier: { tierEnum, tierName }, division, lp: mmr, fullLp: mmr };
}
